import React, {ReactElement, useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {handleElementsClickedAction} from '../../store/actions/chalkBoardElements.actions';
import {
    activeSequenceStepSelector,
    currentSlideIndexSelector,
    sequenceQueueSelector,
    visibleSlideSelector,
} from '../../store/selectors/presentation.selectors';
import {modalSlideIdSelector} from '../../store/selectors/modal';
import {getElementShadow} from '../../incase-domain/common/elementShadow';
import {useSlidePreviewCapture} from '../../context/SlidePreviewCaptureContext';
import './element.css';
import Animated from './types/animated/animated';
import ElementButton from './types/button/button';
import FormElementComponent from './types/form/formElement';
import Circle from './types/geometry/circle';
import Rectangle from './types/geometry/rectangle';
import ElementTextInput from './types/input/textInput';
import ElementImage from './types/media/image';
import ElementVideo from './types/media/video';
import ElementModalComponent from './types/modal/modal';
import {MultiselectQuestionElement} from './types/question/multiSelect/MultiselectQuestionElement';
import YesNoQuestionElement from './types/question/yesNoQuestion/YesNoQuestionElement';
import ElementStaticList from './types/static.list';
import ElementStaticTable from './types/table/v1/static.table';
import ElementStaticTableV2 from './types/table/v2/staticTableV2';
import ElementHtml from './types/text/html';
import ElementStaticText from './types/text/static.text';
import ElementImageGallery from './types/media/imageGallery';
import ElementLineComponent from './types/geometry/line';
import TriangleElement from './types/geometry/TriangleElement';
import StarElement from './types/geometry/StarElement';
import ArrowElement from './types/geometry/ArrowElement';
import QuestionReportElement from './types/question/questionReport/QuestionReportElement';
import SequenceQuestionElement from './types/question/sequence/SequenceQuestionElement';
import {IElement} from '../../incase-domain/elements';
import {
    ElementInteraction,
    LikertScaleInteractions,
    MarkupImageElementInteractions,
    QuestionElementInteractions,
    QuestionMatchPairInteractions,
    SequenceQuestionInteraction,
    YesNoElementInteractions,
} from '../../incase-domain/proptypes/runfile';
import {clickableElementTypes, ELEMENT} from '../../incase-domain/elements/base/ElementTypes';
import {AnimationString} from '../../incase-domain/common/animationString';
import MatchPairQuestionElement from './types/question/matchPair/MatchPairQuestionElement';
import ImageMarkupQuestionElement from './types/question/imageMarkup/ImageMarkupQuestionElement';
import ElementFlipCardComponent from './types/flipCard/flipCard';
import {DeviceType} from '../../incase-domain/common/DeviceType.enum';
import LikertScaleQuestionElement from './types/question/likertScale/LikertScaleQuestionElement';
import QuestionElement from './types/question/question/QuestionElement';
import PhotoAnswerQuestionElement from './types/question/photoAnswer/PhotoAnswerQuestionElement';
import CalculatorElementComponent from './types/calculator/CalculatorElementComponent';
import YesNoQuestionElementV2 from './types/question/yesNoQuestionV2/YesNoQuestionElementV2';
import ElementLineV2Component from './types/geometry/LineElementV2';
import ArrowElementV2 from './types/geometry/ArrowElementV2';
import ChatbotElementComponent from './types/chatbot/ChatbotElementComponent';
import Ellipse from './types/geometry/ellipse';

interface ElementProps {
    element: IElement;
    elementInteractions: ElementInteraction;
    savePresentationRunFile: (elementRunFile: ElementInteraction, newPoints?: number) => void;
    shouldDisplay: boolean;
    device?: DeviceType;
}

function Element(props: ElementProps): ReactElement {
    const {element, device = DeviceType.DESKTOP, elementInteractions, savePresentationRunFile, shouldDisplay} = props;

    const dispatch = useDispatch();
    const isSlidePreviewCapture = useSlidePreviewCapture();

    const [appear, setAppear] = useState(false);
    const [sequenceAnimKey, setSequenceAnimKey] = useState(0);
    const [activeSequenceAnimation, setActiveSequenceAnimation] = useState<AnimationString | undefined>(undefined);
    const {appearAfter} = element;

    const slides = useSelector(visibleSlideSelector);
    const currentSlideIndex = useSelector(currentSlideIndexSelector);
    const modalSlideId = useSelector(modalSlideIdSelector);
    const activeSequenceStep = useSelector(activeSequenceStepSelector);
    const sequenceQueue = useSelector(sequenceQueueSelector);

    const currentSlide = useMemo(() => (slides ? slides[currentSlideIndex] : undefined), [currentSlideIndex, slides]);
    const shouldElementAnimate = useMemo(
        () => currentSlide?._id === element.slideId || modalSlideId === element.slideId,
        [currentSlide?._id, element.slideId, modalSlideId]
    );

    const hasSequenceAnimation = !!element.elementAnimations?.sequence;

    useEffect(() => {
        if (!hasSequenceAnimation) return;
        if (!activeSequenceStep) return;
        const stillPending = sequenceQueue.some((q) => q.elementId === element.id);
        const isActiveStep = activeSequenceStep.elementId === element.id;
        if (!stillPending && !isActiveStep) {
            setAppear(true);
        }
    }, [activeSequenceStep, element.id, hasSequenceAnimation, sequenceQueue]);

    let elementComponent;

    useEffect(() => {
        if (hasSequenceAnimation)
            return () => {
                // noop
            };

        let timeOut: ReturnType<typeof setTimeout>;

        if (appearAfter && appearAfter > 0 && !isSlidePreviewCapture) {
            timeOut = setTimeout(() => {
                setAppear(true);
            }, appearAfter * 1000);
        } else {
            setAppear(true);
        }

        return () => {
            timeOut && clearTimeout(timeOut);
        };
    }, [appear, setAppear, appearAfter, hasSequenceAnimation, isSlidePreviewCapture]);

    useEffect(() => {
        if (!activeSequenceStep || activeSequenceStep.elementId !== element.id)
            return () => {
                // noop
            };

        const anim = element.elementAnimations?.sequence;
        if (!anim)
            return () => {
                // noop
            };

        setAppear(true);
        setActiveSequenceAnimation(anim);
        setSequenceAnimKey((k) => k + 1);

        const SEQUENCE_ANIM_DURATION_MS = 1250;
        const timer = setTimeout(() => {
            setActiveSequenceAnimation(undefined);
        }, SEQUENCE_ANIM_DURATION_MS);

        return () => {
            clearTimeout(timer);
        };
    }, [activeSequenceStep, element.id, element.elementAnimations?.sequence]);

    const elementClickHandler = useCallback(
        (element: IElement) => {
            if (clickableElementTypes.includes(element.type)) {
                dispatch(handleElementsClickedAction([element.id], element.clearAfterClicked ?? false));
            }
        },
        [dispatch]
    );

    switch (element.type) {
        case ELEMENT.STATIC_TEXT:
            elementComponent = <ElementStaticText device={device} element={element} />;
            break;
        case ELEMENT.HTML:
            elementComponent = <ElementHtml element={element} device={device} />;
            break;
        case ELEMENT.STATIC_LIST:
            elementComponent = <ElementStaticList device={device} element={element} />;
            break;
        case ELEMENT.STATIC_TABLE:
            elementComponent = <ElementStaticTable device={device} element={element} />;
            break;
        case ELEMENT.STATIC_TABLE_V2:
            elementComponent = <ElementStaticTableV2 element={element} />;
            break;
        case ELEMENT.IMAGE:
            elementComponent = <ElementImage element={element} />;
            break;
        case ELEMENT.IMAGE_GALLERY:
            elementComponent = <ElementImageGallery element={element} />;
            break;
        case ELEMENT.VIDEO:
            elementComponent = <ElementVideo element={element} />;
            break;
        case ELEMENT.QUESTION:
            elementComponent = (
                <QuestionElement
                    element={element}
                    device={device}
                    elementInteractions={elementInteractions as QuestionElementInteractions}
                    savePresentationRunFile={savePresentationRunFile}
                />
            );

            break;
        case ELEMENT.PHOTO_ANSWER_QUESTION:
            elementComponent = (
                <PhotoAnswerQuestionElement
                    element={element}
                    elementInteractions={elementInteractions as QuestionElementInteractions}
                    savePresentationRunFile={savePresentationRunFile}
                    device={device}
                />
            );

            break;
        case ELEMENT.YES_NO_QUESTION:
            elementComponent = (
                <YesNoQuestionElement
                    element={element}
                    device={device}
                    elementInteractions={elementInteractions as YesNoElementInteractions}
                />
            );
            break;
        case ELEMENT.YES_NO_QUESTION_V2:
            elementComponent = (
                <YesNoQuestionElementV2
                    element={element}
                    elementInteractions={elementInteractions as QuestionElementInteractions}
                    savePresentationRunFile={savePresentationRunFile}
                    device={device}
                />
            );
            break;
        case ELEMENT.SEQUENCE_QUESTION:
            elementComponent = (
                <SequenceQuestionElement
                    element={element}
                    elementInteractions={elementInteractions as SequenceQuestionInteraction}
                    savePresentationRunFile={savePresentationRunFile}
                    device={device}
                />
            );
            break;
        case ELEMENT.MULTI_SELECT:
            elementComponent = (
                <MultiselectQuestionElement
                    element={element}
                    elementInteractions={elementInteractions as QuestionElementInteractions}
                    savePresentationRunFile={savePresentationRunFile}
                    device={device}
                />
            );
            break;
        case ELEMENT.MATCH_PAIR_QUESTION:
            elementComponent = (
                <MatchPairQuestionElement
                    element={element}
                    elementInteractions={elementInteractions as QuestionMatchPairInteractions}
                    savePresentationRunFile={savePresentationRunFile}
                    device={device}
                />
            );
            break;
        case ELEMENT.LIKERT_SCALE:
            elementComponent = (
                <LikertScaleQuestionElement
                    element={element}
                    elementInteractions={elementInteractions as LikertScaleInteractions}
                    savePresentationRunFile={savePresentationRunFile}
                    device={device}
                />
            );
            break;
        case ELEMENT.QUESTION_REPORT:
            elementComponent = <QuestionReportElement element={element} device={device} />;
            break;
        case ELEMENT.RECTANGLE:
            elementComponent = <Rectangle element={element} />;
            break;
        case ELEMENT.CIRCLE:
            elementComponent = <Circle element={element} />;
            break;
        case ELEMENT.ELLIPSE:
            elementComponent = <Ellipse element={element} />;
            break;
        case ELEMENT.LINE:
            elementComponent = <ElementLineComponent element={element} />;
            break;
        case ELEMENT.LINE_V2:
            elementComponent = <ElementLineV2Component element={element} />;
            break;
        case ELEMENT.TRIANGLE:
            elementComponent = <TriangleElement element={element} />;
            break;
        case ELEMENT.STAR:
            elementComponent = <StarElement element={element} />;
            break;
        case ELEMENT.ARROW:
            elementComponent = <ArrowElement element={element} />;
            break;
        case ELEMENT.ARROW_V2:
            elementComponent = <ArrowElementV2 element={element} />;
            break;
        case ELEMENT.BUTTON:
            elementComponent = <ElementButton device={device} element={element} />;
            break;
        case ELEMENT.TEXT_INPUT:
            elementComponent = <ElementTextInput device={device} element={element} savePresentationRunFile={savePresentationRunFile} />;
            break;
        case ELEMENT.FORM:
            elementComponent = <FormElementComponent device={device} element={element} />;
            break;
        case ELEMENT.MODAL:
            elementComponent = <ElementModalComponent element={element} />;
            break;
        case ELEMENT.IMAGE_MARKUP:
            elementComponent = (
                <ImageMarkupQuestionElement
                    element={element}
                    savePresentationRunFile={savePresentationRunFile}
                    elementInteractions={elementInteractions as MarkupImageElementInteractions}
                    device={device}
                />
            );
            break;
        case ELEMENT.FLIP_CARD:
            elementComponent = <ElementFlipCardComponent device={device} element={element} />;
            break;
        case ELEMENT.CALCULATOR:
            elementComponent = <CalculatorElementComponent device={device} element={element} />;
            break;
        case ELEMENT.CHATBOT:
            elementComponent = <ChatbotElementComponent element={element} />;
            break;
        default:
            elementComponent = <div>Invalid element</div>;
    }

    const entryAnimation = element.elementAnimations?.entry;
    const currentAnimation = activeSequenceAnimation ?? (sequenceAnimKey > 0 ? undefined : entryAnimation);

    return (
        <Animated
            key={sequenceAnimKey}
            style={{width: '100%', height: '100%', position: 'relative'}}
            animationIn={currentAnimation}
            isVisible={appear && shouldDisplay && shouldElementAnimate}
        >
            <div
                onClick={() => {
                    elementClickHandler(element);
                }}
                style={{
                    width: '100%',
                    height: '100%',
                    ...getElementShadow(element.boxShadow, element.questionBackgroundBorderRadius),
                }}
            >
                {elementComponent}
            </div>
        </Animated>
    );
}
export default Element;
